"use client";

import { useState } from "react";
import { Send, AlertCircle } from "lucide-react";
import { useCommsPanel } from "../CommsProvider";

interface WhatsAppComposerProps {
  onSent: () => void;
}

export function WhatsAppComposer({ onSent }: WhatsAppComposerProps) {
  const { activeLeadId } = useCommsPanel();
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSend() {
    if (!body.trim() || !activeLeadId || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`/api/leads/${activeLeadId}/timeline/whatsapp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: body.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: { message: "Failed to send WhatsApp" } }));
        setError(err.error?.message ?? `Failed to send WhatsApp (${res.status})`);
        return;
      }
      setBody("");
      onSent();
    } catch {
      setError("Network error — could not send WhatsApp");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="border-t border-gray-200 bg-white px-3 py-3">
      <p className="mb-2 text-xs font-medium text-green-700">Send WhatsApp</p>
      {error && (
        <div className="mb-2 flex items-start gap-1.5 rounded-lg bg-red-50 px-2 py-1.5">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-red-500" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm resize-none focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
        placeholder="Type your message..."
        onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleSend(); }}
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[10px] text-gray-400">Ctrl+Enter to send</span>
        <button
          onClick={handleSend}
          disabled={!body.trim() || sending}
          className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
          {sending ? "Sending..." : "Send WhatsApp"}
        </button>
      </div>
    </div>
  );
}
